import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { MenuItem } from '@material-ui/core';
import InsurancesActions from 'store/ducks/insurances';
import TextField from 'components/TextField';
import Button from 'components/Button';

import { InputAdornment } from '@material-ui/core';
import { Container, Error, Row as StyledRow, Column, WrapperInputCustom } from './styles';
import getInsurance from 'services/getInsurance';

// Componente wrapper para InputMask que preserva a posição do cursor (igual ao usado em ViagemForm)
const MaskedInput = ({ mask, value, onChange, onBlur, children, ...props }) => {
  const handleChange = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const oldValue = value || '';
    const newValue = input.value || '';

    onChange(e);

    setTimeout(() => {
      if (input && input.setSelectionRange) {
        const lengthDiff = newValue.length - oldValue.length;
        let newPosition = cursorPosition;

        if (lengthDiff > 0) {
          newPosition = Math.min(cursorPosition + lengthDiff, newValue.length);
        } else if (lengthDiff < 0) {
          newPosition = Math.max(0, cursorPosition + lengthDiff);
        }

        newPosition = Math.min(newPosition, newValue.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <InputMask
      {...props}
      mask={mask}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      alwaysShowMask={false}
      maskChar={null}
    >
      {children}
    </InputMask>
  );
};

function Insurance({ formik, edit, view }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { insurances } = useSelector((state) => state.insurances);
  const [cpfCnpjMask, setCpfCnpjMask] = useState('999.999.999-99'); // Default to CPF mask
  const cnpjPreenchidoRef = useRef(false); // Flag para evitar loop

  useEffect(() => {
    dispatch(InsurancesActions.getInsurancesRequest());
  }, [dispatch]);

  // Carregar dados do seguro quando estiver editando e type_insurance já estiver preenchido
  useEffect(() => {
    if (edit && formik.values.type_insurance && insurances && insurances.length > 0) {
      // Se houver selected_insurance_id, carregar os dados do seguro
      if (formik.values.selected_insurance_id) {
        handleSelectInsurance(formik.values.selected_insurance_id);
      }
    }
  }, [edit, formik.values.type_insurance, formik.values.selected_insurance_id, insurances]);

  // Garantir que type_insurance seja mantido quando o componente monta em modo de edição
  const typeInsuranceInitializedRef = useRef(false);
  useEffect(() => {
    if (edit && formik.initialValues && formik.initialValues.type_insurance && !typeInsuranceInitializedRef.current) {
      typeInsuranceInitializedRef.current = true;
      // Garantir que o valor inicial seja mantido
      if (formik.values.type_insurance !== formik.initialValues.type_insurance) {
        formik.setFieldValue('type_insurance', formik.initialValues.type_insurance, false);
        console.log('🔵 [Insurance] type_insurance inicializado:', formik.initialValues.type_insurance);
      }
    }
  }, [edit, formik.initialValues, formik.values.type_insurance]);

  /**
   * Preencher CPF/CNPJ automaticamente quando responsável for emitente
   * Usa useRef para evitar loop infinito
   */
  const previousResponsavelRef = useRef(null);
  const previousCnpjRef = useRef(null);
  
  useEffect(() => {
    // Inicializar refs na primeira execução
    if (previousResponsavelRef.current === null) {
      previousResponsavelRef.current = formik.values.insurance_responsavel || '';
      previousCnpjRef.current = formik.values.cnpj || '';
      return; // Não executar lógica na primeira vez
    }
    
    const responsavelMudou = previousResponsavelRef.current !== formik.values.insurance_responsavel;
    const cnpjMudou = previousCnpjRef.current !== formik.values.cnpj;
    
    // Só executar se responsável ou CNPJ mudaram
    if (responsavelMudou || cnpjMudou) {
      previousResponsavelRef.current = formik.values.insurance_responsavel || '';
      previousCnpjRef.current = formik.values.cnpj || '';
      
      // Só executar se responsável for emitente e CNPJ estiver disponível
      if (formik.values.insurance_responsavel === 'emitente' && formik.values.cnpj) {
        const cnpjLimpo = formik.values.cnpj.replace(/\D/g, '');
        if (cnpjLimpo.length === 14) {
          // Verificar se o valor já está preenchido e correto para evitar loop
          const valorAtual = formik.values.insurance_cpf_cnpj_contratante?.replace(/\D/g, '') || '';
          
          // Só atualizar se o valor atual for diferente do CNPJ
          if (valorAtual !== cnpjLimpo) {
            // Formatar CNPJ
            const cnpjFormatado = cnpjLimpo.replace(
              /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
              '$1.$2.$3/$4-$5'
            );
            
            cnpjPreenchidoRef.current = true; // Marcar como preenchido
            formik.setFieldValue('insurance_cpf_cnpj_contratante', cnpjFormatado, false);
            setCpfCnpjMask('99.999.999/9999-99');
          }
        }
      } else if (formik.values.insurance_responsavel !== 'emitente') {
        // Resetar flag quando não for emitente
        cnpjPreenchidoRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.insurance_responsavel, formik.values.cnpj]);

  const handleSelectInsurance = (insuranceId) => {
    const selectedInsurance = insurances.find((i) => i.id === insuranceId);
    if (selectedInsurance) {
      formik.setFieldValue('insurance_codigo', selectedInsurance.codigo || '');
      formik.setFieldValue('insurance_nome_seguradora', selectedInsurance.nome_seguradora || '');
      formik.setFieldValue('insurance_cnpj_seguradora', selectedInsurance.cnpj_seguradora || '');
      formik.setFieldValue('insurance_numero_apolice', selectedInsurance.numero_apolice || '');
    }
  };

  const insuranceOptions = [
    { text: 'Selecione um seguro', value: '' },
    ...insurances.map((insurance) => ({
      text: `${insurance.codigo || ''} - ${insurance.nome_seguradora || ''}`,
      value: insurance.id,
    })),
  ];

  return (
    <Container>
      <StyledRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Tipo do Seguro"
              name="type_insurance"
              select
              value={formik.values.type_insurance || ''}
              onChange={(e) => {
                const value = e.target.value;
                formik.setFieldValue('type_insurance', value);
                formik.setFieldTouched('type_insurance', true);
               // getInsurance(value, formik, insurances);
                //formik.setFieldError('type_insurance', '');
                console.log('Tipo do seguro selecionado:', value);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.type_insurance &&
                Boolean(formik.errors.type_insurance)
              }
              helperText={formik.touched.type_insurance && formik.errors.type_insurance}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value="">Sem seguro</MenuItem>
              <MenuItem value="carga">Carga</MenuItem>
              <MenuItem value="cargaroubo">Carga e Roubo</MenuItem>
            </TextField>
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Valor Seguro"
              name="insurance_value"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              placeholder="0,00"
              value={formik.values.insurance_value || ''}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldError('insurance_value', '');
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.insurance_value && Boolean(formik.errors.insurance_value)}
              helperText={formik.touched.insurance_value && formik.errors.insurance_value}
              disabled={view}
              fullWidth
              color="secondary"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>
      </StyledRow>

      <StyledRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Selecionar Seguro Cadastrado"
              name="selected_insurance_id"
              select
              value={formik.values.selected_insurance_id || ''}
              onChange={(e) => {
                formik.setFieldValue('selected_insurance_id', e.target.value);
                handleSelectInsurance(e.target.value);
              }}
              onBlur={formik.handleBlur}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            >
              {insuranceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </TextField>
          </WrapperInputCustom>
        </Column>
        <Column>
          <WrapperInputCustom style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              label="Novo Seguro"
              onClick={() => history.push('/insurances/new')}
              type="secondary"
              width="150px"
              height="36px"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>
      </StyledRow>

      <StyledRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Código *"
              name="insurance_codigo"
              value={formik.values.insurance_codigo || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.insurance_codigo &&
                Boolean(formik.errors.insurance_codigo)
              }
              helperText={formik.touched.insurance_codigo && formik.errors.insurance_codigo}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Nome da Seguradora *"
              name="insurance_nome_seguradora"
              value={formik.values.insurance_nome_seguradora || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.insurance_nome_seguradora &&
                Boolean(formik.errors.insurance_nome_seguradora)
              }
              helperText={formik.touched.insurance_nome_seguradora && formik.errors.insurance_nome_seguradora}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>
      </StyledRow>

      <StyledRow>
        <Column>
          <WrapperInputCustom>
            <MaskedInput
              mask="99.999.999/9999-99"
              value={formik.values.insurance_cnpj_seguradora || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="insurance_cnpj_seguradora"
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  label="CNPJ da Seguradora *"
                  error={
                    formik.touched.insurance_cnpj_seguradora &&
                    Boolean(formik.errors.insurance_cnpj_seguradora)
                  }
                  helperText={formik.touched.insurance_cnpj_seguradora && formik.errors.insurance_cnpj_seguradora}
                  fullWidth
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </MaskedInput>
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Número da Apólice *"
              name="insurance_numero_apolice"
              value={formik.values.insurance_numero_apolice || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.insurance_numero_apolice &&
                Boolean(formik.errors.insurance_numero_apolice)
              }
              helperText={formik.touched.insurance_numero_apolice && formik.errors.insurance_numero_apolice}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>
      </StyledRow>

      <StyledRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Responsável pelo Seguro *"
              name="insurance_responsavel"
              select
              value={formik.values.insurance_responsavel || ''}
              onChange={(e) => {
                formik.handleChange(e);
                // Se selecionar emitente, preencher automaticamente o CPF/CNPJ
                if (e.target.value === 'emitente' && formik.values.cnpj) {
                  const cnpjLimpo = formik.values.cnpj.replace(/\D/g, '');
                  if (cnpjLimpo.length === 14) {
                    const cnpjFormatado = cnpjLimpo.replace(
                      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                      '$1.$2.$3/$4-$5'
                    );
                    formik.setFieldValue('insurance_cpf_cnpj_contratante', cnpjFormatado);
                    setCpfCnpjMask('99.999.999/9999-99');
                  }
                }
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.insurance_responsavel &&
                Boolean(formik.errors.insurance_responsavel)
              }
              helperText={formik.touched.insurance_responsavel && formik.errors.insurance_responsavel}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value="">Selecione</MenuItem>
              <MenuItem value="emitente">Emitente</MenuItem>
              <MenuItem value="tomador">Tomador do Serviço / Contratante</MenuItem>
            </TextField>
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <MaskedInput
              mask={cpfCnpjMask}
              value={formik.values.insurance_cpf_cnpj_contratante || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length > 11) {
                  setCpfCnpjMask('99.999.999/9999-99');
                } else {
                  setCpfCnpjMask('999.999.999-99');
                }
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              name="insurance_cpf_cnpj_contratante"
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  label="CPF/CNPJ do Contratante *"
                  error={
                    formik.touched.insurance_cpf_cnpj_contratante &&
                    Boolean(formik.errors.insurance_cpf_cnpj_contratante)
                  }
                  helperText={formik.touched.insurance_cpf_cnpj_contratante && formik.errors.insurance_cpf_cnpj_contratante}
                  fullWidth
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </MaskedInput>
          </WrapperInputCustom>
        </Column>
      </StyledRow>
    </Container>
  );
}

export default Insurance;
